/*
 * Copyright (c) 2020 Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { Formik, Form, FieldArray } from "formik";

// @material-ui/core components
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { CheckBoxOutlined, CheckBox } from "@material-ui/icons";
import { Badge } from "@material-ui/core";

import { submitExButton, checkboxListForBool } from "components/Form/FormField";
import {
  TestActionParams,
  TestResult,
  reportCorrect,
  reportWrong,
  reportFeedback,
  showHtmlText,
} from "./ExMain";

import exerciseStyle from "assets/jss/views/exercise/exerciseStyle.jsx";
import { testType } from "client/reducers/infoboxReducer";
import { sRightAns } from "assets/jss/views/ResStrings";
const styles = createStyles(exerciseStyle);

interface Props extends WithStyles<typeof styles> {
  classes: any;
  exercise: any;
  testResult: TestResult;
  quit: boolean;
  onAction: (params: TestActionParams) => void;
}

const ExSelBase: React.FC<Props> = ({
  classes,
  exercise,
  testResult,
  quit,
  onAction,
}: Props) => {
  const [s_rand, setRandom] = useState([]);
  const [s_report, setReport] = useState<React.ReactNode>(null);

  useEffect(() => {
    if (exercise) {
      let rand;
      if (testResult) {
        // testResult could be {} due to incompleted test, fill the missed stuff
        rand = testResult.rand || exercise.e_ans.map((x: any, i: number) => i);
        setReport(
          renderReport(
            testResult.userAnsBool || exercise.e_ans.map(() => false),
            rand,
          ),
        );
      } else {
        rand = exercise.e_text.map((t: string, idx: number) => idx);
        rand.sort(() => Number(Math.random() - 0.5));
      }
      setRandom(rand);
    }
  }, [exercise]);

  useEffect(() => {
    if (quit) {
      onAction({
        action: "quit",
        data: { rand: s_rand },
      });
    }
  }, [quit]);

  const renderTestBody = (values: any) => {
    const aCode = "A".charCodeAt(0);
    const choicesList = values.userAns.map((ans: boolean, idx: number) => ({
      name:
        String.fromCharCode(aCode + idx) + ". " + exercise.e_text[s_rand[idx]],
    }));

    return (
      <FieldArray
        name="userAns"
        render={checkboxListForBool({
          dataList: choicesList,
          valueSet: values.userAns,
          valueName: "userAns",
          single: exercise.type === testType.SEL_SINGLE,
        })}
      />
    );
  };

  const checkAns = (userAns: boolean[], bankAns: boolean[]) =>
    !userAns.some((ua: boolean, i: number) => ua !== bankAns[i]);

  const renderReport = (userAns: boolean[], rand: number[]) => {
    const { e_ans } = exercise;
    const aCode = "A".charCodeAt(0);
    const chkIcon = (
      <CheckBoxOutlined color="inherit" style={{ marginTop: "3px" }} />
    );
    const chkIcon2 = (
      <CheckBox color="secondary" style={{ marginTop: "3px" }} />
    );

    // bankAns: correct answer viewed from user
    const bankAns = e_ans.map((ans: Boolean, i: number) => e_ans[rand[i]]);
    const isCorrect = checkAns(userAns, bankAns);

    // render user's answer, if user didn't skipped
    let userAnsList: Array<React.ReactNode> = [];
    if (testResult.userAnsBool) {
      userAns.forEach((ans: boolean, idx: number) => {
        if (ans) {
          const correct = userAns[idx] === bankAns[idx];

          userAnsList.push(
            <div key={idx} className={classes.reportLine}>
              <Badge
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                badgeContent={correct ? <>&#x2713;</> : "X"}
                color={correct ? "secondary" : "error"}>
                {chkIcon}&nbsp;
              </Badge>
              {showHtmlText(
                String.fromCharCode(aCode + idx) +
                  ". " +
                  exercise.e_text[rand[idx]],
                classes,
              )}
            </div>,
          );
        }
      });
    }

    let correctAnsList: Array<React.ReactNode> = [];
    if (!isCorrect) {
      bankAns.forEach((ans: boolean, idx: number) => {
        if (ans === true) {
          correctAnsList.push(
            <div key={idx} className={classes.reportLine}>
              {chkIcon2}&nbsp;
              {showHtmlText(
                String.fromCharCode(aCode + idx) +
                  ". " +
                  exercise.e_text[rand[idx]],
                classes,
              )}
            </div>,
          );
        }
      });
    }

    let feedbackList: Array<React.ReactNode> = [];
    bankAns.forEach((ans: boolean, idx: number) => {
      const feedbk = exercise.e_feedback[rand[idx]];
      if (feedbk !== "" && (ans === true || ans !== userAns[idx])) {
        feedbackList.push(
          <div key={idx} className={classes.reportLine}>
            {showHtmlText(
              `<b>${String.fromCharCode(aCode + idx)}</b>. ${feedbk}`,
              classes,
            )}
          </div>,
        );
      }
    });
    const feedback: React.ReactNode = feedbackList.length ? (
      <>{feedbackList}</>
    ) : null;

    return (
      <>
        {isCorrect
          ? reportCorrect(classes)
          : reportWrong(classes, !testResult.userAnsBool)}
        {userAnsList.length !== 0 && userAnsList}
        {!isCorrect && (
          <>
            <div className={classes.reportCorrect}>{sRightAns}</div>
            {correctAnsList}
          </>
        )}
        {(feedback !== null || exercise.explain !== "") &&
          reportFeedback(feedback, exercise.explain, exercise.fbk, classes)}
      </>
    );
  };

  // const resetSubmit = (actions: any, item: string) => {
  //   actions.setSubmitting(false);
  //   actions.setTouched({ [item]: false });
  //   actions.validateForm(false);
  // };

  return (
    <div className={classes.exContainer}>
      {showHtmlText(exercise.statement, classes)}
      <Formik
        initialValues={{
          userAns:
            testResult && testResult.userAnsBool
              ? testResult.userAnsBool
              : exercise.e_ans.map(() => false),
        }}
        enableReinitialize={true}
        onSubmit={(values, formikBag) => {
          const { e_ans } = exercise;
          formikBag.resetForm({ userAns: e_ans.map(() => false) });

          // bankAns: correct answer viewed from user
          const bankAns = e_ans.map((x: any, i: number) => e_ans[s_rand[i]]);
          const correct = checkAns(values.userAns, bankAns);
          onAction({
            action: "next",
            data: { rand: s_rand, userAnsBool: values.userAns, correct },
          });
        }}
        render={({ values, isValid, isSubmitting }) => {
          return (
            <Form>
              {s_rand.length && !testResult && renderTestBody(values)}
              {(!testResult && submitExButton("Next", !isValid)) ||
                isSubmitting}
              {s_report}
            </Form>
          );
        }}
      />
    </div>
  );
};

export const ExSel = withStyles(styles)(ExSelBase);
